import './mail-in-process-actions.ajscss';

import { AgentDesktopRouter } from '@agent/agent-interface/agent-desktop-router';
import { AbstractEpticaController } from '@agent/agent-interface/contact-view/mail/abstract-mail-process';
import { IEpticaDeprecatedService } from '@agent/core/channel-digit/mail/eptica/eptica-deprecated.service';
import { REJECTION_PENDING_ASK_FOR_HELP, EpticaHelper } from '@agent/core/channel-digit/mail/eptica/eptica.helper';
import { EpticaHelperJson } from '@agent/core/channel-digit/mail/eptica/eptica.helper.json';
import { EpticaData } from '@agent/core/channel-digit/mail/eptica/models/eptica-data';
import {
  HOME_PAGE_NAME,
  IEpticaJsonResponse,
  IEpticaRequestFrame,
  REQUEST_PAGE_NAME,
} from '@agent/core/channel-digit/mail/eptica/models/eptica.request.interfaces';
import { IMemoService } from '@agent/core/channel-digit/mail/memo/memo.service';
import { ISmsDeprecatedService } from '@agent/core/channel-digit/sms/sms-deprecated.service';
import { IAppointmentDeprecatedService } from '@agent/core/contact/appointment-deprecated.service';
import { IContactDeprecatedService } from '@agent/core/contact/contact-deprecated.service';
import { IUnitOutgoingCallDeprecatedService } from '@agent/core/phone/outcall/unit-outgoing-call-deprecated.service';
import { DisplayModeService } from '@agent/core/settings/display-mode.service';
import { SessionDeprecatedService } from '@commons/core/auth/session-deprecated.service';
import { ToastDeprecatedService } from '@commons/core/notification/toast-deprecated.service';
import { ChannelUtil } from '@commons/util/helper/channel';
import { ng1Destroy, RxUtil } from '@commons/util/utilities/rx';
import { Actions, ofType } from '@ngrx/effects';
import { IComponentController, IComponentOptions, ILogService, IPromise, IQService } from 'angular';
import { i18n } from 'i18next';
import { take } from 'rxjs';

import {
  BookingMailAction,
  BookingMailFacade,
  ChanEnum,
  Channel,
  ChannelDirection,
  ChannelType,
  Color,
  Customer,
  CustomerCenterMode,
  CustomerFacade,
  EpticaExposedRequest,
  Option,
  PhoneValidationService,
  TranslateService,
  ToasterFacade,
} from '@contact/upgrade';

import templateUrl from './mail-in-process-actions.template.html';
import { AgentBookinMailInprocessState } from './state/agent-booking-mail-routing.module';

const MODULE_NAME = 'contactMailInProcessActions';

enum sendState {
  SUCCESS = 'success',
  FAIL = 'fail',
}

class MailInProcessActionsCtrl extends AbstractEpticaController<IEpticaRequestFrame> implements IComponentController {
  public name = MODULE_NAME;
  readonly AgentBookinMailInprocessState = AgentBookinMailInprocessState;

  public helpzoneOpen: boolean;
  public hasMailApproval = false;
  public isMultipleActionDisplay = false;
  public sentAskForHelp = false;
  public receivedAskForHelp = false;
  public answeredAskForHelp = false;
  public approvalRequest = false;
  public rejectedApprovalRequest = false;
  public isSupFeatureEnabled = true;
  public havePhoneAddress = false;
  private requestPromise: IPromise<IEpticaRequestFrame>;
  public epticaDisplayState: string;
  public isRecipientListValid = true;
  public disableAction = false;
  private _request: IEpticaRequestFrame;
  // to avoid loading of global namespace NodeJS for only NodeJS.Timeout
  private _frameInterval: any;

  constructor(
    override readonly $log: ILogService,
    override readonly $q: IQService,
    private readonly $uibModal: any,
    private readonly $i18next: i18n,
    override readonly contactDeprecatedService: IContactDeprecatedService,
    private readonly epticaDeprecatedService: IEpticaDeprecatedService,
    private readonly smsDeprecatedService: ISmsDeprecatedService,
    private readonly appointmentDeprecatedService: IAppointmentDeprecatedService,
    private readonly memoService: IMemoService,
    private readonly sessionDeprecatedService: SessionDeprecatedService,
    private readonly unitOutgoingCallDeprecatedService: IUnitOutgoingCallDeprecatedService,
    override readonly toastDeprecatedService: ToastDeprecatedService,
    private readonly phoneValidation: PhoneValidationService,
    private readonly toasterFacade: ToasterFacade,
    private readonly configuration: any,
    private readonly bookingMailFacade: BookingMailFacade,
    private readonly customerFacade: CustomerFacade,
    private readonly displayModeService: DisplayModeService,
    private readonly actions$: Actions,
    override readonly router: AgentDesktopRouter,
    override readonly translateService: TranslateService
  ) {
    super($log, $q, contactDeprecatedService, toastDeprecatedService, router, translateService);
  }

  get outcallRequested(): boolean {
    if (this.unitOutgoingCallDeprecatedService.unitOutgoingCall != null) {
      return this.unitOutgoingCallDeprecatedService.unitOutgoingCall.requested;
    }
    return false;
  }

  get isApprovalRequest(): boolean {
    return this.approvalRequest || this.rejectedApprovalRequest;
  }

  get isAskForHelpRequest(): boolean {
    return this.receivedAskForHelp || this.answeredAskForHelp;
  }

  /**
   * Send the mail (submit eptica's form)
   */
  send(): void {
    if (!this.hasMailApproval) {
      // Add the CC and BCC recipients into contact
      this.mainPromise().then(request => request.addCcAndCccContacts());
      // Send the mail
      this.handleJsonAction(request => request.send()).then(response => this.openCustomerCard(response));
    }
  }

  /**
   * Open customer card search view
   * @param response send email request response
   */
  openCustomerCard(response: IEpticaJsonResponse) {
    if (response.messageState === sendState.SUCCESS) {
      this.contactDeprecatedService.getContact(this.responseId).then(contact => {
        if (!contact || contact.customerId) {
          this.$log.info('[mail-in-process-actions] No need to open customer card.');
          return;
        }
        // prepare CustomerCenterDialogParam for presumption (=>query]
        const uri = contact.phoneNumber ?? '';
        const channel = this.toNormalizeChannel(contact?.channel);
        this.customerFacade.presumptionDialog({
          uri,
          channel: ChannelUtil.toString(channel),
          query: contact.phoneNumber,
        });
      });
    }
  }

  /**
   * Put on hold with or without sending an answer
   */
  async defer(): Promise<void> {
    const request = await this.mainPromise();
    if (!request.isMessageTextEmpty()) {
      this.memoService.openMemoForm(
        this.responseId,
        this.disableAction,
        this.isRecipientListValid,
        () =>
          this.handleJsonAction(request =>
            request.sendDefer().then(r => {
              this.deferWithResponse(this.responseId);
              return r;
            })
          ),
        () => request.defer().then(() => this.endContact(this.responseId).finally(() => request.finish()))
      );
    } else {
      this.toasterFacade.alert({
        message: 'mail:error.requiredFieldMessage',
      });
    }
  }

  /**
   * Open eptica's delegation panel (and close others) in js (no submit)
   */
  requestDelegate(): void {
    this.saveDraft().finally(() => {
      this.mainPromise().then(request =>
        this.epticaDeprecatedService
          .loadRequestUntil({
            requestId: request.getId(),
            responseId: this.responseId,
            forceReload: true,
          })
          .then(() => {
            this.router.navigateFromLastChild(['..', AgentBookinMailInprocessState.forward]);
          })
      );
    });
  }

  /**
   * Perform the delegation of the mail (submit eptica's form)
   */
  delegate(): void {
    this.handleJsonAction(request => request.delegate());
  }

  /**
   * Send and delegate the mail (submit eptica's form)
   */
  sendDelegate(): void {
    this.mainPromise().then(request => {
      this.showActions = false;
      request.hide();
      return request.sendDefer().then(
        epticaJson => {
          this.displayModeService.helpzone$.pipe(ng1Destroy(this)).subscribe(helpzoneOpen => (this.helpzoneOpen = helpzoneOpen));
          EpticaHelperJson.displayActionMessages(this.toastDeprecatedService, epticaJson);
          return this.epticaDeprecatedService
            .loadRequestUntil({
              requestId: request.getId(),
              responseId: this.responseId,
              forceReload: true,
            })
            .then(() => this.requestDelegate());
        },
        () => {
          this.showActions = true;
          request.show();
          this.$q.reject(request);
        }
      );
    });
  }

  /**
   * Close eptica's delegation panel (and open answer panel) in js (no submit)
   */
  cancelDelegate(): void {
    this.doJsAction(['..'], request => request.toggleAnswerPanel(true));
  }

  /**
   * Open eptica's closing panel (and close others) in js (no submit)
   */
  requestClose(): void {
    if (this.sessionDeprecatedService.hasPermission('CSR_MAIL_INCOMING::contact:close_without_response_with_comment')) {
      this.doJsAction(['..', AgentBookinMailInprocessState.close], request => request.toggleClosePanel(true));
    } else {
      this.mainPromise().then(request =>
        request
          .close(request.closeComment)
          .then(() => this.endContact(this.responseId))
          .then(() => request.finish())
      );
    }
  }

  /**
   * Close the mail (submit eptica's form)
   */
  close(): void {
    this.mainPromise().then(request =>
      request
        .close(request.closeComment)
        .then(() => this.endContact(this.responseId))
        .then(() => request.finish())
    );
  }

  /**
   * Close eptica's closing panel (and open answer panel) in js (no submit)
   */
  cancelClose(): void {
    if (this._request) {
      this._request.closeComment = '';
      this._request.show();
    }
    this.doJsAction(['..'], request => request.toggleClosePanel(false));
  }

  /**
   * Open review popup (and close other panels) in js (no submit)
   */

  requestAskForReview(): void {
    this.mainPromise().then(request => {
      // check if the email is empty
      let result = request.requestAskForReview();
      if (result) {
        this.$uibModal.open({
          component: 'popupReview',
          keyboard: false,
          ariaLabelledBy: 'modal-header',
          ariaDescribedBy: 'modal-body',
          resolve: {
            validateFn: () => comment => this.validateAskForReview(comment),
          },
        });
      }
    });
  }

  validateAskForReview(comment: string): void {
    this.mainPromise().then(request => {
      request.validateRequestAskForReview(comment).then(success => {
        if (success) {
          this.askForReview();
        }
      });
    });
  }

  /**
   * Ask for the review of the mail (submit eptica's form)
   */
  askForReview(): void {
    this.handleJsonAction(
      request => request.askForReview().then(response => this.contactDeprecatedService.getContact(this.responseId).then(() => response)),
      undefined,
      false,
      true
    );
  }

  /**
   * Close the eptica's closing popup (and open answer panel)
   */
  cancelAskForReview(): void {
    this.doJsAction(['..'], request => request.cancelAskForReview());
  }

  /**
   * Open assist form
   */
  requestAssist(): void {
    this.doJsAction(['..', AgentBookinMailInprocessState.askForHelp], request => request.toggleAssistPanel(true));
  }

  /**
   * Open assist form
   */
  cancelAssist(): void {
    this.doJsAction(['..'], request => request.cancelAssist());
  }

  /**
   * Assist
   */
  assist(): void {
    this.handleJsonAction(
      request => this.contactDeprecatedService.getContact(this.responseId).then(contact => request.assist(contact.epticaRequestId || 0)),
      'ASK_FOR_HELP'
    );
  }

  cancelApproval(): void {
    this.endContact(this.responseId, Option.of('CANCEL_APPROVAL'));
  }

  acceptApproval(): void {
    this.handleJsonAction(request => request.acceptApproval(), 'APPROVAL_ACCEPTED').then(() =>
      this.endContact(this.responseId, Option.of('APPROVAL_ACCEPTED'))
    );
  }

  requestRejectApproval(): void {
    this.doJsAction(['..', AgentBookinMailInprocessState.rejectReview], request =>
      request.requestRejectApproval(this.cancelRejectApproval.bind(this), this.rejectApproval.bind(this))
    );
  }

  rejectApproval(): void {
    this.handleJsonAction(request => request.rejectApproval(), 'APPROVAL_REJECTED').then(
      () => this.endContact(this.responseId, Option.of('APPROVAL_REJECTED')),
      () => this.toasterFacade.alert({ message: this.$i18next.t('agent:toast.reject_approval_require') })
    );
  }

  cancelRejectApproval(): void {
    this.doJsAction(['..', AgentBookinMailInprocessState.acceptReview], request => request.cancelRejectApproval());
  }

  /**
   * Open the popin that allow the csr the search for pre-answered response
   */
  openKnowlegdeDialog(): void {
    this.doJsAction(['..', AgentBookinMailInprocessState.knowledge], request =>
      request.openKnowledgeDialog(this.cancelKnowlegdeDialog.bind(this), this.validateKnowlegdeDialog.bind(this))
    );
  }

  /**
   * Forward a request to queue (bounce a request). The connected account must be
   * the responsible of request or a supervisor of responsible of request.
   */
  bounceRequest = (): void => {
    this.mainPromise().then(request =>
      request.bounce(this.responseId).then(
        () => this.contactDeprecatedService.refuse(this.responseId, 'BOUNCE').then(() => request.finish()),
        error => {
          if (error && error.cause === REJECTION_PENDING_ASK_FOR_HELP) {
            // Send was not done because the popup that asks to confirm to close all the pending
            // assistance requests has opened. => override the vanilla behaviour by replacing the buttons
            request.overrideClosePendingAssistJson(() =>
              this.handleJsonAction(() => request.confirmClosePendingAssistJson(error.submitForm), 'BOUNCE')
            );
          }
        }
      )
    );
  };

  giveUp(): void {
    if (this.sentAskForHelp) {
      this.toastDeprecatedService.alert('agent:contact.workspace.mail.error.abandon_mail_with_ask_for_help_error');
      return;
    }
    if (this.sessionDeprecatedService.hasPermission('CSR_MAIL_INCOMING::contact:gave_up_comment')) {
      this.bookingMailFacade.openFormBounce(this.responseId, {
        param: {
          type: 'BOUNCE',
          responseId: this.responseId,
        },
      });
    } else {
      this.bookingMailFacade.bounce(this.responseId, {
        param: {
          type: 'BOUNCE',
          comment: '',
          responseId: this.responseId,
        },
      });
    }
    this.mainPromise().then(request =>
      this.actions$.pipe(ofType(BookingMailAction.BounceSuccess, BookingMailAction.BounceFail), take(1)).subscribe(() => request.finish())
    );
  }

  /**
   * Insert the selected pre-answered response
   * (simulate click on the eptica button)
   */
  validateKnowlegdeDialog(): void {
    this.doJsAction(['..'], request => request.validateKnowlegdeDialog());
  }

  /**
   * Close the knowledge dialog without inserting anything
   * (simulate click on the eptica button)
   */
  cancelKnowlegdeDialog() {
    this.doJsAction(['..'], request => request.cancelKnowlegdeDialog());
  }

  sendAskForHelpAnswer() {
    this.handleJsonAction(request => request.sendAskForHelpAnswer(request.getId()), 'ASK_FOR_HELP');
  }

  cancelAskForHelpAnswer() {
    this.showActions = false;

    return this.mainPromise().then(request => {
      request.hide();
      this.contactDeprecatedService.refuse(this.responseId, 'TECH_FAILURE').finally(() => request.finish());
    });
  }

  checkEpticaDataAfterReload(request: IEpticaRequestFrame, epticaData: EpticaData, refuseReason: Option<string>): IPromise<EpticaData> {
    if (epticaData.pageName === HOME_PAGE_NAME) {
      this.endContact(this.responseId, refuseReason).finally(() => request.finish());
    } else if (epticaData.pageName === REQUEST_PAGE_NAME) {
      // Nothing to do, probably a server side validation error from eptica
      this.showActions = true;

      // Hide the answer panel since it's a validation error on another action (like assist, delegate, ..)
      if (this.isState(AgentBookinMailInprocessState.send)) {
        if (this.isState(AgentBookinMailInprocessState.answerAskForHelp)) {
          request.addPrintButtonForAssists();
        }
        request.toggleAnswerPanel(false);
      }

      // Show the request
      request.show();
    } else {
      this.contactDeprecatedService.cancel(this.responseId).finally(() => this.mainPromise().then(r => r.finish()));
    }
    return this.$q.resolve(epticaData);
  }

  openSMSForm(): void {
    this.contactDeprecatedService.getSelectedContact().then(contact => {
      contact.customerId && this.smsDeprecatedService.openSMSForContact(contact.customerId);
    });
  }

  openAppointmentForm(): void {
    this.contactDeprecatedService.getContact(this.responseId).then(contact => {
      this.contactDeprecatedService.getCustomer(this.responseId).then(customer => {
        this.contactDeprecatedService.getCustomerAddresses(this.responseId).then(addresses => {
          let phoneNumber: string = contact.phoneNumber;
          let firstName: string;
          let lastName: string;
          if (!!customer) {
            firstName = customer.firstname;
            lastName = customer.lastname;
            addresses.forEach(_addr => {
              if (_addr.channel.type === ChannelType.PHONE && _addr.channel.direction === ChannelDirection.INCOMING) {
                phoneNumber = _addr.uri;
              }
            });
          } else {
            firstName = contact.customerName;
            lastName = '';
          }
          if (!this.phoneValidation.isValid(phoneNumber)) {
            phoneNumber = '';
          }
          this.appointmentDeprecatedService.openAppointmentForm(this.responseId, phoneNumber, firstName, lastName, contact.skills);
        });
      });
    });
  }

  saveDraft(): IPromise<EpticaData> {
    return this.mainPromise().then(request => {
      request.hide();
      return request.saveDraft();
    });
  }

  hasBothDeferOptions(): boolean {
    return (
      this.sessionDeprecatedService.hasPermission('CSR_MAIL_INCOMING::contact:mail_defer') &&
      this.sessionDeprecatedService.hasPermission('CSR_MAIL_INCOMING::contact:mail_send_defer')
    );
  }

  hasAtLeastOneDeferPermission() {
    return (
      this.sessionDeprecatedService.hasPermission('CSR_MAIL_INCOMING::contact:mail_defer') ||
      this.sessionDeprecatedService.hasPermission('CSR_MAIL_INCOMING::contact:mail_send_defer')
    );
  }

  canOpenOutcall(): boolean {
    return this.unitOutgoingCallDeprecatedService.canOpenOutcall();
  }

  outcallDisabled(): boolean {
    return this.unitOutgoingCallDeprecatedService.outcallDisabled();
  }

  /**
   * Open the Call component
   */
  openOutcall() {
    this.openOutcallAction();
  }

  mainPromise(): IPromise<IEpticaRequestFrame> {
    return this.requestPromise;
  }

  checkCustomerCardAdressePhone() {
    this.contactDeprecatedService.getCustomerAddresses(this.responseId).then(addresses => {
      let found = false;
      addresses.forEach(add => {
        if (add.channel.type === ChannelType.PHONE && add.channel.direction === ChannelDirection.INCOMING) {
          found = true;
        }
      });
      if (found) {
        this.havePhoneAddress = true;
      } else {
        this.havePhoneAddress = false;
      }
    });
  }

  override $onInit(): void {
    super.$onInit();
    this.displayModeService.helpzone$.pipe(ng1Destroy(this)).subscribe(helpzoneOpen => (this.helpzoneOpen = helpzoneOpen));
    this.isSupFeatureEnabled = this.sessionDeprecatedService.isSupFeatureEnabled;

    this.showActions = false;
    this.requestPromise = this.contactDeprecatedService
      .getContact(this.responseId)
      .then(contact => {
        this.checkCustomerCardAdressePhone();

        this.hasMailApproval = contact.mailApproval;
        // in the case of transferred mail with opened ask for help, the contact.epticaSentAskForHelpRequest not equals true,
        // so we use new method in epticaDeprecatedService
        this.epticaDeprecatedService
          .isRequestWithOpenedAskForHelp((contact.epticaRequestId || 0).toString())
          .pipe(ng1Destroy(this))
          .subscribe(sent => (this.sentAskForHelp = sent));
        this.receivedAskForHelp = contact.epticaReceivedAskForHelpRequest;
        this.answeredAskForHelp = contact.epticaAnsweredAskForHelpRequest;
        this.approvalRequest = contact.epticaApprovalRequest;
        this.rejectedApprovalRequest = contact.epticaRejectedApprovalRequest;
        return this.epticaDeprecatedService.loadRequest({
          requestId: contact.epticaRequestId || 0,
          responseId: this.responseId,
        });
      })
      .then(request =>
        RxUtil.toPromise(request.requestDto$, this.$q).then(dto => {
          this._request = request;
          request.hide();
          request.setSubjectContactStyle();
          request.setCCContactStyle();
          request.setBCCContactStyle();
          request.setRecipientContactStyle();
          request.setChannelContactStyle();

          // DO NOT REMOVE ! FIX for FF : need to show the frame before changing the request
          // since the Area-InProcess-MailZone we want to target may not be in the DOM yet, we have to wait for it to be loaded
          // the loading of the component is concurrent to this one so it's only a matter of async execution for the DOM node to be loaded
          // the interval is a security measure in case things go too slow
          let tries = 0;
          this._frameInterval = setInterval(() => {
            if (tries++ > 5) {
              request.show(document.getElementById('Area-WorkZone-Core')!);
              request = this.updateRequest(request, dto);
              clearInterval(this._frameInterval);
            } else {
              Option.of(document.getElementById('Area-InProcess-MailZone'))
                // if present show frame in this container
                .sideEffect(container => {
                  request.show(container);
                  request = this.updateRequest(request, dto);
                  clearInterval(this._frameInterval);
                });
            }
          }, 10);
          // we listen for changes of the mail composition validity
          request.recipientsValidity$.pipe(ng1Destroy(this)).subscribe(valid => (this.isRecipientListValid = valid));
          return request;
        })
      )
      .catch(error => {
        this.$log.error(error);
        this.contactDeprecatedService.refuse(this.responseId, 'TECH_FAILURE');
        return this.$q.reject('');
      });
  }

  updateRequest(request: IEpticaRequestFrame, dto: EpticaExposedRequest): IEpticaRequestFrame {
    this.showActions = true;

    const assigned = dto.accountResponsible && dto.accountResponsible.id === this.epticaDeprecatedService.getMailAccountId();
    if (this.receivedAskForHelp && assigned && this.isState(AgentBookinMailInprocessState.answerAskForHelp)) {
      this.router.navigateFromLastChild(['..']);
    } else if (this.receivedAskForHelp && !assigned) {
      request.addPrintButtonForAssists();
      request.uniformUploadButtonForAskForHelp();
      request.appearRequestAnswerAssistance();
    } else if (
      this.approvalRequest &&
      (this.isState(AgentBookinMailInprocessState.acceptReview) || this.isState(AgentBookinMailInprocessState.rejectReview))
    ) {
      request.hideAttachmentInAnswerZone();
      request.customizeAnswerDisplay();
    } else if (assigned || request.hasOpenedPopup() || this.receivedAskForHelp) {
      request.customizeAnswerDisplay();
    } else {
      this.$log.error(`[mail-in-process-actions] The request[${request.getId()}] is not assigned to current user.`);
      this.$log.debug('[mail-in-process-actions] Eptica pageName is ' + request.getPageName());
      this.contactDeprecatedService.refuse(this.responseId, 'TECH_FAILURE').finally(() => request.finish());
    }

    request.toggleConversationalMode();

    // sets 2 custom classes on the iframe "html" markup: one for color, one for layout disposition
    request.addCustomClass(Color.BLUE);
    request.addCustomClass(this.getLayoutClass());
    request.addClassToEditorFormContainer();

    request.printMessageClientWithHtmlFormat();

    return request;
  }

  getLayoutClass(): string {
    let layoutClass = '';
    if (this.configuration && this.configuration.mail && this.configuration.mail.columnMode) {
      layoutClass = 'two-columns';
    }

    return layoutClass;
  }

  $onDestroy(): void {
    clearInterval(this._frameInterval);
    this.mainPromise().then(request => request.hide());
  }

  private openOutcallAction(): void {
    if (!this.outcallRequested) {
      this.retrieveCustomerCardWithResponseId()
        .then(card => this.unitOutgoingCallDeprecatedService.openUnitOutgoingCallFormFromInteraction(this.responseId, card))
        .then(() => this.unitOutgoingCallDeprecatedService.finishAndClosePauseAndTask());
    }
  }

  private retrieveCustomerCardWithResponseId(): IPromise<Customer> {
    let deferred = this.$q.defer<Customer>();
    this.contactDeprecatedService.getCustomer(this.responseId).then(
      customer => {
        if (!!customer) {
          deferred.resolve(customer);
        } else {
          deferred.reject();
        }
      },
      () => {
        deferred.reject();
      }
    );
    return deferred.promise;
  }

  private toNormalizeChannel(channel: Channel): Channel {
    if (ChannelUtil.equals(channel, ChannelUtil.INCOMING_BASKET)) {
      return ChannelUtil.INCOMING_MAIL;
    }
    return channel;
  }
}

export class MailInProcessActions implements IComponentOptions {
  templateUrl = templateUrl;
  bindings = {
    responseId: '<',
  } as { [binding: string]: string };
  controller = [
    '$log',
    '$q',
    '$uibModal',
    '$i18next',
    'contactDeprecatedService',
    'epticaDeprecatedService',
    'smsDeprecatedService',
    'appointmentDeprecatedService',
    'memo',
    'sessionDeprecatedService',
    'unitOutgoingCallDeprecatedService',
    'toastDeprecatedService',
    'phoneValidation',
    'toasterFacade',
    'configuration',
    'bookingMailFacade',
    'customerFacade',
    'displayModeService',
    'ngrxActionService',
    'agentDesktopRouter',
    'translateService',
    MailInProcessActionsCtrl,
  ];
}

export default MODULE_NAME;
